import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from '../../../api';
import BrandKitEditor, { isValidBrandKit } from '../BrandKitEditor';
import { BRAND_KIT_ROLES, BrandKit, VideoProject } from '../types';
import { pillButtonSx, vb } from '../theme';
import { apiMessage } from '../useAutosave';

// Prototype screen 3: review the org brand kit, optionally edit it (saved to
// Organisation settings AND copied into this project's snapshot).
const BrandKitStep: React.FC<{
  project: VideoProject;
  orgKit: BrandKit;
  isFirstTime: boolean;
  onBack: () => void;
  onContinue: (project: VideoProject) => void;
}> = ({ project, orgKit, isFirstTime, onBack, onContinue }) => {
  const kit = project.brandKitSnapshot ?? orgKit;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<BrandKit>(kit);
  const [saving, setSaving] = useState(false);

  const saveAndContinue = async () => {
    if (!isValidBrandKit(draft)) {
      toast.error('Colors must be hex values like #1C1C1C.');
      return;
    }
    try {
      setSaving(true);
      await API.updateOrganizationBrandKit({ brandKit: draft });
      const res: any = await API.updateVideoProject({ projectId: project.id, patch: { brandKitSnapshot: draft } });
      toast.success('Brand kit saved to Organization settings.');
      onContinue(res.data as VideoProject);
    } catch (err) {
      toast.error(apiMessage(err, 'The brand kit could not be saved. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontFamily: vb.font, fontSize: 20, fontWeight: 800, color: vb.text }}>
          Your brand kit
        </Typography>
        <Box
          sx={{
            fontFamily: vb.font,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.1em',
            px: 0.7,
            py: 0.25,
            borderRadius: '4px',
            background: isFirstTime ? vb.brand : vb.border,
            color: isFirstTime ? '#1C1C1C' : vb.textSecondary
          }}
        >
          {isFirstTime ? 'FIRST TIME' : 'FROM ORG SETTINGS'}
        </Box>
      </Box>
      <Typography sx={{ fontFamily: vb.font, fontSize: 13.5, color: vb.textSecondary, mt: -1.5 }}>
        {isFirstTime
          ? 'No brand kit yet. These defaults will be saved to Organization settings when you continue.'
          : 'Explainer scenes use these four colors. Adjust them here or continue.'}
      </Typography>

      {editing ? (
        <BrandKitEditor value={draft} onChange={setDraft} />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
          {BRAND_KIT_ROLES.map((r) => (
            <Box
              key={r.key}
              sx={{ borderRadius: '10px', border: `1px solid ${vb.border}`, overflow: 'hidden', background: vb.page }}
              data-testid={`brand-role-${r.key}`}
            >
              <Box sx={{ height: 64, background: kit[r.key] }} />
              <Box sx={{ p: 1.25 }}>
                <Typography sx={{ fontFamily: vb.font, fontSize: 13, fontWeight: 700, color: vb.text }}>
                  {r.label}
                </Typography>
                <Typography sx={{ fontFamily: vb.font, fontSize: 11.5, color: vb.textTertiary }}>{r.hint}</Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: vb.textSecondary, mt: 0.5 }}>
                  {kit[r.key]}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Typography sx={{ fontFamily: vb.font, fontSize: 12.5, color: vb.textTertiary }}>
        Every template is built from exactly these four parts, so your colors land the same way on every scene. Fonts
        and logo stay standard for now.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={editing ? () => setEditing(false) : onBack} sx={pillButtonSx()} disabled={saving}>
          Previous
        </Button>
        <Box sx={{ flex: 1 }} />
        {editing ? (
          <>
            <Typography sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary, mr: 1 }}>
              Saved to Organization settings. Every future video starts here.
            </Typography>
            <Button
              data-testid="brand-save-continue"
              onClick={saveAndContinue}
              disabled={saving}
              sx={pillButtonSx(true)}
            >
              {saving ? 'Saving...' : 'Save and Continue'}
            </Button>
          </>
        ) : (
          <>
            <Button data-testid="brand-edit" onClick={() => setEditing(true)} sx={pillButtonSx()}>
              Edit Brand Kit
            </Button>
            <Button
              data-testid="brand-continue"
              onClick={isFirstTime ? saveAndContinue : () => onContinue(project)}
              disabled={saving}
              sx={pillButtonSx(true)}
            >
              {saving ? 'Saving...' : 'Continue with This Kit'}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default BrandKitStep;
